import {
  japaneseSurnames, chineseSurnames, arabicSurnames, portugueseSurnames,
  japaneseSurnamePronunciation, chineseSurnamePronunciation, arabicSurnamePronunciation, portugueseSurnamePronunciation,
  japaneseNamePronunciation, chineseNamePronunciation, englishNamePronunciation,
  spanishNamePronunciation, frenchNamePronunciation, germanNamePronunciation,
  arabicNamePronunciation, portugueseNamePronunciation,
  russianNamePronunciation, thaiNamePronunciation,
  koreanSurnamePronunciation, surnameMap,
  maleNames, femaleNames, maleDescriptions, femaleDescriptions, flagImageMap
} from '../mocks/nameData';

interface NameAnalysis {
  syllableCount: number;
  hasStrongConsonant: boolean;
  hasSoftConsonant: boolean;
  vowelTone: 'bright' | 'neutral' | 'dark';
  difficulty: 'easy' | 'medium' | 'hard';
  impression: 'soft' | 'neutral' | 'strong';
  romanized: string;
  surname: string;
  givenName: string;
}

interface NameResult {
  country: string;
  flag: string;
  name: string;
  pronunciation: string;
  description: string;
}

// 한국어 자음/모음 분석
const STRONG_CONSONANTS = ['ㄱ', 'ㄷ', 'ㅂ', 'ㅅ', 'ㅈ', 'ㄲ', 'ㄸ', 'ㅃ', 'ㅆ', 'ㅉ'];
const SOFT_CONSONANTS = ['ㅇ', 'ㄴ', 'ㄹ', 'ㅁ'];
const BRIGHT_VOWELS = ['ㅏ', 'ㅗ', 'ㅑ', 'ㅛ'];
const NEUTRAL_VOWELS = ['ㅐ', 'ㅔ', 'ㅣ', 'ㅒ', 'ㅖ'];
const DARK_VOWELS = ['ㅓ', 'ㅜ', 'ㅡ', 'ㅕ', 'ㅠ'];

// 한글 분해 함수
function decomposeHangul(char: string): { initial: string; medial: string; final: string } | null {
  const code = char.charCodeAt(0) - 0xAC00;
  if (code < 0 || code > 11171) return null;

  const initialIndex = Math.floor(code / 588);
  const medialIndex = Math.floor((code % 588) / 28);
  const finalIndex = code % 28;

  const initials = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
  const medials = ['ㅏ', 'ㅐ', 'ㅑ', 'ㅒ', 'ㅓ', 'ㅔ', 'ㅕ', 'ㅖ', 'ㅗ', 'ㅘ', 'ㅙ', 'ㅚ', 'ㅛ', 'ㅜ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅠ', 'ㅡ', 'ㅢ', 'ㅣ'];
  const finals = ['', 'ㄱ', 'ㄲ', 'ㄳ', 'ㄴ', 'ㄵ', 'ㄶ', 'ㄷ', 'ㄹ', 'ㄺ', 'ㄻ', 'ㄼ', 'ㄽ', 'ㄾ', 'ㄿ', 'ㅀ', 'ㅁ', 'ㅂ', 'ㅄ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];

  return {
    initial: initials[initialIndex],
    medial: medials[medialIndex],
    final: finals[finalIndex]
  };
}

// 한국 이름 분석
export function analyzeKoreanName(name: string): NameAnalysis {
  const chars = name.split('');
  let hasStrongConsonant = false;
  let hasSoftConsonant = false;
  let brightCount = 0;
  let neutralCount = 0;
  let darkCount = 0;

  chars.forEach(char => {
    const decomposed = decomposeHangul(char);
    if (!decomposed) return;

    if (STRONG_CONSONANTS.includes(decomposed.final)) hasStrongConsonant = true;
    if (SOFT_CONSONANTS.includes(decomposed.final)) hasSoftConsonant = true;

    if (BRIGHT_VOWELS.includes(decomposed.medial)) brightCount++;
    else if (NEUTRAL_VOWELS.includes(decomposed.medial)) neutralCount++;
    else if (DARK_VOWELS.includes(decomposed.medial)) darkCount++;
  });

  const vowelTone = brightCount > darkCount ? 'bright' : (darkCount > brightCount ? 'dark' : 'neutral');
  const impression = hasStrongConsonant ? 'strong' : (hasSoftConsonant ? 'soft' : 'neutral');

  const firstChar = chars[0] || '김';
  const surname = surnameMap[firstChar] || 'Kim';
  const givenName = chars.slice(1).join('');

  return {
    syllableCount: chars.length,
    hasStrongConsonant,
    hasSoftConsonant,
    vowelTone,
    difficulty: hasStrongConsonant ? 'hard' : 'easy',
    impression,
    romanized: name,
    surname,
    givenName
  };
}

// 시드 기반 선택 함수
function seededSelect<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// 일본어 이름 생성 (한국 성 사용 안 함)
function generateJapaneseName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const surname = seededSelect(japaneseSurnames, seed);
  const givenName = seededSelect(names.japan, seed);
  
  return {
    name: `${surname} ${givenName}`,
    pronunciation: `${japaneseSurnamePronunciation[surname] || surname} ${japaneseNamePronunciation[genderKey][givenName] || givenName}`
  };
}

// 중국어 이름 생성 (한국 성 사용 안 함)
function generateChineseName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const surname = seededSelect(chineseSurnames, seed);
  const givenName = seededSelect(names.china, seed);
  
  return {
    name: `${surname} ${givenName}`,
    pronunciation: `${chineseSurnamePronunciation[surname] || surname} ${chineseNamePronunciation[genderKey][givenName] || givenName}`
  };
}

// 영어 이름 생성 (현지 이름 + 한국 성)
function generateEnglishName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.english, seed);
  
  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${englishNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 스페인어 이름 생성 (현지 이름 + 한국 성)
function generateSpanishName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.spanish, seed);
  
  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${spanishNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 프랑스어 이름 생성 (현지 이름 + 한국 성)
function generateFrenchName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.french, seed);
  
  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${frenchNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 독일어 이름 생성 (현지 이름 + 한국 성)
function generateGermanName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.german, seed);
  
  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${germanNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 아랍어 이름 생성 (이름만 사용, 성 제외)
function generateArabicName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.arabic, seed);
  
  return {
    name: givenName,
    pronunciation: `${arabicNamePronunciation[genderKey][givenName] || givenName}`
  };
}

// 포르투갈어 이름 생성 (현지 이름 + 한국 성)
function generatePortugueseName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;
  
  const givenName = seededSelect(names.portuguese, seed);
  
  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${portugueseNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 러시아어 이름 생성 (현지 이름 + 한국 성)
function generateRussianName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;

  const givenName = seededSelect(names.russian, seed);

  return {
    name: `${givenName} ${analysis.surname}`,
    pronunciation: `${russianNamePronunciation[genderKey][givenName] || givenName} ${koreanSurnamePronunciation[analysis.surname] || analysis.surname}`
  };
}

// 태국어 이름 생성 (이름만 사용, 성 제외)
function generateThaiName(analysis: NameAnalysis, gender: 'male' | 'female', seed: number): { name: string; pronunciation: string } {
  const genderKey = gender === 'male' ? 'male' : 'female';
  const names = gender === 'male' ? maleNames : femaleNames;

  const givenName = seededSelect(names.thai, seed);

  return {
    name: givenName,
    pronunciation: `${thaiNamePronunciation[genderKey][givenName] || givenName}`
  };
}

// 글로벌 이름 생성
export function generateGlobalNames(inputName: string, gender: 'male' | 'female', analysis: NameAnalysis): NameResult[] {
  const baseSeed = hashString(inputName + gender);
  const descriptions = gender === 'male' ? maleDescriptions : femaleDescriptions;

  const japanese = generateJapaneseName(analysis, gender, baseSeed);
  const chinese = generateChineseName(analysis, gender, baseSeed + 1);
  const english = generateEnglishName(analysis, gender, baseSeed + 2);
  const spanish = generateSpanishName(analysis, gender, baseSeed + 3);
  const french = generateFrenchName(analysis, gender, baseSeed + 4);
  const german = generateGermanName(analysis, gender, baseSeed + 5);
  const arabic = generateArabicName(analysis, gender, baseSeed + 6);
  const portuguese = generatePortugueseName(analysis, gender, baseSeed + 7);
  const russian = generateRussianName(analysis, gender, baseSeed + 8);
  const thai = generateThaiName(analysis, gender, baseSeed + 9);

  return [
    {
      country: '일본',
      flag: '🇯🇵',
      name: japanese.name,
      pronunciation: japanese.pronunciation,
      description: seededSelect(descriptions.japan, baseSeed)
    },
    {
      country: '중국',
      flag: '🇨🇳',
      name: chinese.name,
      pronunciation: chinese.pronunciation,
      description: seededSelect(descriptions.china, baseSeed + 1)
    },
    {
      country: '미국',
      flag: '🇺🇸',
      name: english.name,
      pronunciation: english.pronunciation,
      description: seededSelect(descriptions.english, baseSeed + 2)
    },
    {
      country: '스페인',
      flag: '🇪🇸',
      name: spanish.name,
      pronunciation: spanish.pronunciation,
      description: seededSelect(descriptions.spanish, baseSeed + 3)
    },
    {
      country: '프랑스',
      flag: '🇫🇷',
      name: french.name,
      pronunciation: french.pronunciation,
      description: seededSelect(descriptions.french, baseSeed + 4)
    },
    {
      country: '독일',
      flag: '🇩🇪',
      name: german.name,
      pronunciation: german.pronunciation,
      description: seededSelect(descriptions.german, baseSeed + 5)
    },
    {
      country: '아랍',
      flag: '🇸🇦',
      name: arabic.name,
      pronunciation: arabic.pronunciation,
      description: seededSelect(descriptions.arabic, baseSeed + 6)
    },
    {
      country: '브라질',
      flag: '🇧🇷',
      name: portuguese.name,
      pronunciation: portuguese.pronunciation,
      description: seededSelect(descriptions.portuguese, baseSeed + 7)
    },
    {
      country: '러시아',
      flag: '🇷🇺',
      name: russian.name,
      pronunciation: russian.pronunciation,
      description: seededSelect(descriptions.russian, baseSeed + 8)
    },
    {
      country: '태국',
      flag: '🇹🇭',
      name: thai.name,
      pronunciation: thai.pronunciation,
      description: seededSelect(descriptions.thai, baseSeed + 9)
    }
  ];
}
